import { makeStyles } from '@material-ui/core/styles';
import { useDispatch, useSelector } from 'react-redux';
import { selectAiProduct } from '@store/ai-product/selectors';
import { useRef, useState, useCallback, useMemo, useEffect } from 'react';
import classNames from 'classnames';
import ProductImageEditor from '@pages/ai-product/image-editor/ProductImageEditor';
import ProductImageFormDialog from '@pages/ai-product/ProductImageFormDialog';
import {
  processAiProduct, renderAiProduct,
  updateAiProduct,
} from '@store/ai-product/actions';
import { actions } from '@store/companies';
import { ROUTES } from '@app/routes/routesUrls';
import useNavigate from '@shared/router/useNavigate';
import Button from '@shared/components/button';
import useOnClickOutside from '@shared/hooks';
import Backdrop from '@shared/components/backdrop';
import CircularProgress from '@shared/components/circular-progress';
import ProductTextField from '@pages/ai-product/background-step/ProductTextField';
import EnvironmentTextField from '@pages/ai-product/background-step/EnvironmentTextField';
import LocationTextField from './LocationTextField';
import PlacementTextField from './PlacementTextField';
import StyleTextField from './StyleTextField';
import TextField from '@shared/components/text-field';
import CreateIcon from '@resources/icons/CreateIcon';
import CheckIcon from '@resources/icons/CheckIcon';
import ExpandIcon from '@resources/icons/ExpandIcon';
import APITextField from '@pages/ai-product/APITextField';
import appendUrlParams from '@shared/utils/appendUrlParams';
import LimitBanner from '@shared/components/page-parts/LimitBanner';
import AIProduct from '..';

const useStyles = makeStyles((theme) => ({
  pageWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    fontSize: '14px',
    padding: '16px',
    width: '100%',
    height: '100%',
    overflow: 'auto',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '16px',
    color: '#334D6E',
    fontWeight: 700,
    textDecoration: 'underline',
    '& > div': {
      width: '100%',
      maxWidth: '200px',
    },
    marginBottom: '16px',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '20px',
  },
  contentHeader: {
    display: 'inline-flex',
    flexDirection: 'column',
    gap: '6px',
    fontSize: '20px',
    fontWeight: 700,
  },
  contentSubHeader: {
    color: '#9CACC2',
    fontSize: '16px',
    fontWeight: 400,
  },
  imagesWrapper: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '20px',
  },
  uploadImage: {
    width: '260px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '10px',
    height: '184px',
    objectFit: 'cover',
    border: '1px solid #e2e2e2',
    borderRadius: '4px',
    flex: '0 0 260px',
    position: 'relative',
    '& > img': {
      width: '100%',
      height: '100%',
      objectFit: 'contain',
    },
  },
  iconsWrapper: {
    position: 'absolute',
    top: '8px',
    right: '8px',
    display: 'flex',
    gap: '8px',
    zIndex: 2,
  },
  iconButton: {
    cursor: 'pointer',
    display: 'flex',
    border: '1px solid #E3E9F1',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#fff',
    borderRadius: '50%',
    padding: '4px',
    '& > svg path': {
      fill: '#9DA3B2',
    },
  },
  selectIcon: {
    position: 'absolute',
    bottom: '8px',
    right: '8px',
    border: 'none',
    zIndex: 2,
    background: '#E3E9F1',
    '& > svg path': {
      fill: '#fff',
      stroke: 'inherit',
    },
    '&.active': {
      background: theme.palette.primary.main,
    },
  },
  imageEditorWrapper: {
    display: 'flex',
    marginTop: '20px',
    gap: '16px',
    flex: '1',
  },
  productImageEditorWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    flex: '1 1 auto',
    maxWidth: '100%',
  },
  submitButton: {
    alignSelf: 'center',
  },
  imageList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    overflow: 'auto',
  },
  actions: {
    display: 'flex',
    marginTop: '50px',
    gap: '16px',
    '& > button': {
      minWidth: '142px',
      width: '100%',
    },
    '& > button:first-child': {
      borderColor: '#192A3E',
      color: '#192A3E',
    },
  },
  toolBarWrapper: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '20px',
    '& > button': {
      minWidth: '200px',
      height: '42px',
      width: '100%',
    },
  },
  buttonCreate: {
    fontSize: '13px',
    fontWeight: 500,
    display: 'inline-flex',
    flexDirection: 'row',
    gap: '10px',
    lineHeight: 'normal',
    verticalAlign: 'middle',
    alignItems: 'center',
    '& > svg': {
      fontSize: '1.8rem',
      '& > path': {
        stroke: '#fff',
      },
    },
    '& > button': {
      maxWidth: 'unset',
    },
  },
  formDialog: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  colorButton: {
    position: 'relative',
    width: '160px',
  },
  colorPicker: {
    position: 'absolute',
    zIndex: 1,
  },
  backdrop: {
    top: 0,
  },
  autocomplete: {
    width: '200px',
    height: '42px',
    '& .MuiAutocomplete-popper > div .MuiAutocomplete-listbox .MuiAutocomplete-option:last-child': {
      border: '1px solid #E2EAF3',
      borderRadius: '4px',
      fontSize: '14px',
      color: '#9CACC2',
      fontStyle: 'italic',
      fontWeight: 400,
      margin: '5px',
    },
  },
  autocompleteGroping: {
    width: '200px',
    height: '42px',
  },
}));

export function BackgroundStep() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { goTo } = useNavigate();
  const settings = useSelector((state) => state.companies.settings);
  const user = useSelector((state) => state.app.user);
  const product = useSelector(selectAiProduct);
  const containerRef = useRef();
  const stageRef = useRef();
  const [isProductImageOpen, setIsProductImageOpen] = useState(false);
  const [activeImage, setActiveImage] = useState(product?.images[0]);
  const [activeImageVariant, setActiveImageVariant] = useState(0);
  const [showColor, setShowColor] = useState(false);
  const [api, setApi] = useState('Stable');
  const { getCurrentSettings } = actions;

  useEffect(() => {
    dispatch(getCurrentSettings(user.company.id));
  }, [dispatch, user, getCurrentSettings]);

  const isAiProductReadOnly = useMemo(() => {
    return !(settings?.studioConfig?.ai_p_active ?? true);
  }, [settings]);

  useOnClickOutside(containerRef, () => {
    setShowColor(false);
  });

  const handleCancel = () => {
    setIsProductImageOpen(false);
  };

  const handleSave = () => {
    dispatch(updateAiProduct(product, null, () => {
      const imageIds = product.images
        .map((image) => image.id);
      if (imageIds.length) {
        dispatch(processAiProduct(product.id, {
          tasks: ['BACKGROUND_GENERATION'],
          images: imageIds.map((id) => ({ imageId: id })),
          isBeta: false,
        }));
      }
    }));
    setIsProductImageOpen(false);
  };

  const handleCreateForImage = (image, lifestyle) => {
    const updatedImage = { ...image, state: 'BACKGROUND_GENERATION' };
    const updatedImages = product.images.map((img) => {
      if (img.id === image.id) {
        updatedImage.lifestyle.forEach((item) => {
          if (item.id === lifestyle.id) {
            lifestyle.state = 'BACKGROUND_GENERATION';
          }
        });
        return updatedImage;
      }
      return img;
    });

    dispatch(updateAiProduct({
      ...product,
      images: updatedImages,
    }, null, () => {
      dispatch(processAiProduct(product.id, {
        tasks: ['BACKGROUND_REGENERATE'],
        images: [{
          imageId: image.id,
          lifestyleId: lifestyle.id,
        }],
        isBeta: api === 'Beta',
      }));
    }));
  };

  const handleSelectImage = (imageId, variantIndex) => {
    const aiImage = product.images.find((image) => {
      return image.id === imageId;
    });
    aiImage.lifestyle[variantIndex].selected = !aiImage.lifestyle[variantIndex].selected;
    dispatch(renderAiProduct({
      ...product,
    }));
  };

  const handleExpandClick = (image, index) => (e) => {
    e.stopPropagation();
    setIsProductImageOpen(true);
    setActiveImage(image);
    setActiveImageVariant(index);
  };

  const handleCreateClick = (image, lifetstyle) => (e) => {
    e.stopPropagation();
    handleCreateForImage(image, lifetstyle);
  };

  const handleCheckClick = (imageId, variantIndex) => (e) => {
    e.stopPropagation();
    handleSelectImage(imageId, variantIndex);
  };

  const handleImageClick = (imageId, variantIndex) => (e) => {
    e.stopPropagation();
    handleSelectImage(imageId, variantIndex);
  };

  const isNextDisabled = !product.images.some((image) => {
    return image.lifestyle.some((lifestyle) => (lifestyle.selected));
  });

  const isEnvironmentValid = useCallback(() => {
    if (!product.environment) return false;
    const requiredFields = ['positionPrompt', 'locationPrompt', 'stylePrompt'];
    return requiredFields.every(field => product?.environment[field] && product?.environment[field]?.trim() !== '');
  }, [product.environment]);

  const imageForTextGen = product.images[0];

  const hasBackgroundGenerationProcess = product.images.some((image) => image.state === 'BACKGROUND_GENERATION');

  return (
    <div className={ classes.pageWrapper }>
      <AIProduct />
      <div className={ classes.header }>
        Product Detail Page Content Pack
        {/* <div>
          <APITextField
            value={ api }
            onChange={ (value) => {
              setApi(value);
            } }
          />
        </div> */}
      </div>
      <LimitBanner show={ isAiProductReadOnly } />
      <div className={ classes.content }>
        <div className={ classes.contentHeader }>
          Step 2. Lifestyle image
          <div className={ classes.contentSubHeader }>
            Select photos that you like and want to save - we’ll remember your choice.
          </div>
        </div>
        <div className={ classes.toolBarWrapper }>
          <div className={ classes.autocompleteGroping }>
            <ProductTextField
              value={ product.category }
              onChange={ (category) => {
                dispatch(renderAiProduct({
                  ...product,
                  category,
                }));
              } }
            />
          </div>
          <div className={ classes.autocomplete }>
            <PlacementTextField
              value={ product.environment?.positionPrompt || '' }
              disabled={ !product.category }
              onChange={ (positionPrompt) => {
                dispatch(renderAiProduct({
                  ...product,
                  environment: {
                    ...product.environment,
                    positionPrompt,
                  },
                }));
              } }
            />
          </div>
          <div className={ classes.autocomplete }>
            <LocationTextField
              value={ product.environment?.locationPrompt || '' }
              disabled={ !product.category }
              onChange={ (locationPrompt) => {
                dispatch(renderAiProduct({
                  ...product,
                  environment: {
                    ...product.environment,
                    locationPrompt,
                  },
                }));
              } }
            />
          </div>
          <div className={ classes.autocomplete }>
            <StyleTextField
              value={ product.environment?.stylePrompt || '' }
              disabled={ !product.category }
              onChange={ (stylePrompt) => {
                dispatch(renderAiProduct({
                  ...product,
                  environment: {
                    ...product.environment,
                    stylePrompt,
                  },
                }));
              } }
            />
          </div>
          {/* <div className={ classes.autocomplete }>
            <TextField
              disabled={ !product.environment }
              value={ product?.environment?.prompts[0] }
              onChange={ (event) => {
                const { environment } = product;
                environment.prompts[0] = event.target.value;
                dispatch(renderAiProduct({
                  ...product,
                  environment,
                }));
              } }
            />
          </div>
          <div className={ classes.autocomplete }>
            <TextField
              disabled={ !product.environment }
              value={ product?.environment?.prompts[1] }
              onChange={ (event) => {
                const { environment } = product;
                environment.prompts[1] = event.target.value;
                dispatch(renderAiProduct({
                  ...product,
                  environment,
                }));
              } }
            />
          </div>
          <div className={ classes.autocomplete }>
            <TextField
              disabled={ !product.environment }
              value={ product?.environment?.prompts[2] }
              onChange={ (event) => {
                const { environment } = product;
                environment.prompts[2] = event.target.value;
                dispatch(renderAiProduct({
                  ...product,
                  environment,
                }));
              } }
            />
          </div> */}
          <Button
            /* eslint-disable max-len */
            disabled={ (!product.category || product.category.name == 'custom') || !isEnvironmentValid() || hasBackgroundGenerationProcess || isAiProductReadOnly }
            onClick={ () => {
              product.images.forEach((image) => {
                image.state = 'BACKGROUND_GENERATION';
              });
              dispatch(updateAiProduct({
                ...product,
              }, null, () => {
                const imageIds = product.images
                  .map((image) => image.id);
                if (imageIds.length) {
                  dispatch(processAiProduct(product.id, {
                    tasks: ['BACKGROUND_GENERATION'],
                    images: imageIds.map((id) => ({ imageId: id })),
                    isBeta: api === 'Beta',
                  }));
                }
              }));
            } }
          >
            <div className={ classes.buttonCreate }>
              <CreateIcon />
              Create
            </div>
          </Button>
        </div>
        <div className={ classes.imagesWrapper }>
          { product.images.map((image) => {
            return (
              <>
                { !!image.lifestyle?.length && image.lifestyle.map((lifestyle, index) => {
                  const isSelected = lifestyle.selected;
                  return (
                    <div
                      key={ `${image.id}${index}` }
                      className={ classes.uploadImage }
                    >
                      <img src={ appendUrlParams(lifestyle.src, { size: 256 }) } />
                      { lifestyle.state === 'BACKGROUND_GENERATION' && (
                      <Backdrop className={ classes.backdrop }>
                        <CircularProgress size="large" />
                        Background Generation...
                      </Backdrop>
                      )}
                      { !hasBackgroundGenerationProcess && (
                      <div className={ classes.iconsWrapper }>
                        <div
                          className={ classes.iconButton }
                          onClick={ handleExpandClick(image, index) }
                        >
                          <ExpandIcon />
                        </div>
                        { !isAiProductReadOnly && (
                        <div
                          className={ classes.iconButton }
                          onClick={ handleCreateClick(image, lifestyle) }
                        >
                          <CreateIcon />
                        </div>
                        )}
                      </div>
                      )}
                    { !hasBackgroundGenerationProcess && (
                      <div
                        className={ classNames(classes.iconButton, classes.selectIcon, { active: isSelected }) }
                        onClick={ handleCheckClick(image.id, index) }
                      >
                        <CheckIcon size="small" />
                      </div>
                    )}
                    </div>
                  );
                })}
                { (!image.lifestyle.length || (
                  image.state === 'BACKGROUND_GENERATION'
                  && !image.lifestyle.some((v) => v.state === 'BACKGROUND_GENERATION')
                )) && (
                  <div
                    key={ image.id }
                    className={ classes.uploadImage }
                  >
                    <img src={ image.siloSrc } />
                    { image.state === 'BACKGROUND_GENERATION' && (
                      <Backdrop className={ classes.backdrop }>
                        <CircularProgress size="large" />
                        Background Generation...
                      </Backdrop>
                    )}
                  </div>
                )}
              </>
            );
          })}
        </div>
        <div className={ classes.actions }>
          <Button
            color="secondary"
            variant="outlined"
            onClick={ () => {
              goTo(`${ROUTES.AI_PRODUCT.ROOT}${ROUTES.AI_PRODUCT.SILO(product.id)}`);
            } }
          >
            Back
          </Button>
          <Button
            onClick={ () => {
              dispatch(updateAiProduct(product, null, () => {
                if (!product.texts.length && !isAiProductReadOnly) {
                  dispatch(processAiProduct(product.id, {
                    tasks: ['TEXT_GENERATION'],
                    images: [{
                      imageId: imageForTextGen.id,
                    }],
                    isBeta: false,
                  }));
                  goTo(`${ROUTES.AI_PRODUCT.ROOT}${ROUTES.AI_PRODUCT.TEXT(product.id)}`);
                } else if (product.texts.length) {
                  goTo(`${ROUTES.AI_PRODUCT.ROOT}${ROUTES.AI_PRODUCT.TEXT(product.id)}`);
                } else {
                  goTo(`${ROUTES.AI_PRODUCT.ROOT}${ROUTES.AI_PRODUCT.CAPTION(product.id)}`);
                }
              }));
            } }
            disabled={ isNextDisabled }
          >
            Next
          </Button>
        </div>
      </div>
      <ProductImageFormDialog
        Form="div"
        FormProps={ {
          className: classes.formDialog,
        } }
        open={ isProductImageOpen }
        content={ (
          <div className={ classes.imageEditorWrapper }>
            {/* <div className={ classes.imageList }>
              {
                  product.images.map((image) => {
                    return (
                      <>
                        {
                            !!image.lifestyle?.length && image.lifestyle.map((lifestyle, index) => {
                              return (
                                <div
                                  key={ `${image.id}${index}` }
                                  onClick={ () => {
                                    setActiveImage(image);
                                    setActiveImageVariant(index);
                                  } }
                                  className={ classNames(classes.uploadImage, { active: image === activeImage && activeImageVariant === index }) }
                                >
                                  <img src={ lifestyle.src } />
                                </div>
                              );
                            })
                        }
                        {
                            !image.lifestyle?.length
                            && (
                            <div
                              key={ image.id }
                              onClick={ () => {
                                setActiveImage(image);
                              } }
                              className={ classNames(classes.uploadImage, { active: image === activeImage }) }
                            >
                              <img src={ image.alphaSrc } />
                            </div>
                            )
                          }
                      </>

                    );
                  })
              }
            </div> */}
            <div className={ classes.productImageEditorWrapper }>
              <ProductImageEditor
                ref={ stageRef }
                image={ activeImage }
                variant={ activeImageVariant }
                mode="BACKGROUND"
              />
            </div>
            <div className={ classes.imageList } />
          </div>
        ) }
        onSave={ handleSave }
        onCancel={ handleCancel }
        onClose={ handleCancel }
        cancelButtonText="Quit"
      />
    </div>
  );
}
